import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  CircleHelp,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../api/api";

export default function FAQDrawer({
  isOpen,
  onClose,
  anchor = { right: "20px", bottom: "90px" },
}) {
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    setLoading(true);
    setQuery("");
    setExpanded(null);

    api
      .get("/faqs")
      .then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        setFaqs(
          rows.filter(
            (faq) =>
              faq.is_active === true ||
              Number(faq.is_active) === 1 ||
              faq.is_active === undefined,
          ),
        );
      })
      .catch((error) => {
        console.error("FAQ loading failed:", error);
        if (!cancelled) setFaqs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return faqs;
    return faqs.filter((faq) =>
      `${faq.question || ""} ${faq.answer || ""}`
        .toLocaleLowerCase()
        .includes(normalizedQuery),
    );
  }, [faqs, query]);

  if (!isOpen) return null;

  return (
    <div className="sf-faq-root">
      <button
        className="sf-faq-overlay"
        onClick={onClose}
        aria-label={t("close", "Fermer")}
      />

      <section
        className="sf-faq-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sf-faq-title"
        style={{
          right: anchor.right || "20px",
          bottom: anchor.bottom || "90px",
          top: anchor.top || "auto",
        }}
      >
        <header className="sf-faq-header">
          <button
            type="button"
            className="sf-faq-icon-button"
            onClick={onClose}
            aria-label={t("back", "Retour")}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="sf-faq-title-block">
            <span>{t("support_faq", "Questions fréquentes")}</span>
            <h2 id="sf-faq-title">{t("helpCenter", "Centre d'aide")}</h2>
          </div>
          <button
            type="button"
            className="sf-faq-icon-button"
            onClick={onClose}
            aria-label={t("close", "Fermer")}
          >
            <X size={19} />
          </button>
        </header>

        <div className="sf-faq-intro">
          <span className="sf-faq-intro-icon">
            <Sparkles size={17} />
          </span>
          <p>
            {t(
              "faq_intro",
              "Trouvez rapidement une réponse sur votre capteur et la plateforme.",
            )}
          </p>
        </div>

        <div className="sf-faq-search">
          <Search size={18} />
          <input
            type="search"
            placeholder={t("whatCanWeHelp", "En quoi pouvons-nous vous aider ?")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label={t("clear", "Effacer")}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="sf-faq-scroll">
          {loading && (
            <div className="sf-faq-loading">
              {t("loadingFaqs", "Chargement des FAQ...")}
            </div>
          )}

          {!loading && faqs.length === 0 && (
            <div className="sf-faq-empty">
              <span>
                <CircleHelp size={31} />
              </span>
              <h3>{t("faq_empty_title", "Aucune question publiée")}</h3>
              <p>
                {t(
                  "faq_empty_hint",
                  "Les réponses de l'équipe SmartFarm apparaîtront ici.",
                )}
              </p>
            </div>
          )}

          {!loading && faqs.length > 0 && filtered.length === 0 && (
            <div className="sf-faq-empty is-search">
              <span>
                <Search size={29} />
              </span>
              <h3>{t("faq_no_match_title", "Aucune réponse trouvée")}</h3>
              <p>
                {t(
                  "faq_no_match_hint",
                  "Essayez un autre mot-clé ou consultez toutes les questions.",
                )}
              </p>
              <button type="button" onClick={() => setQuery("")}>
                {t("faq_show_all", "Afficher toutes les questions")}
              </button>
            </div>
          )}

          {!loading &&
            filtered.map((item) => {
              const isExpanded = expanded === item.id;
              return (
                <article
                  className={`sf-faq-card ${isExpanded ? "is-open" : ""}`}
                  key={item.id}
                >
                  <button
                    type="button"
                    className="sf-faq-question"
                    onClick={() =>
                      setExpanded((previous) =>
                        previous === item.id ? null : item.id,
                      )
                    }
                    aria-expanded={isExpanded}
                  >
                    <span className="sf-faq-question-mark">
                      <CircleHelp size={17} />
                    </span>
                    <strong>{item.question}</strong>
                    <ChevronDown
                      className={isExpanded ? "is-rotated" : ""}
                      size={19}
                    />
                  </button>
                  {isExpanded && <p className="sf-faq-answer">{item.answer}</p>}
                </article>
              );
            })}
        </div>
      </section>

      <style>{`
        .sf-faq-overlay { position: fixed; inset: 0; z-index: 3999; border: 0; background: rgba(16, 35, 49, .52); cursor: default; }
        .sf-faq-drawer { position: fixed; z-index: 4000; display: flex; flex-direction: column; width: min(430px, calc(100vw - 32px)); max-height: min(680px, calc(100vh - 112px)); overflow: hidden; border-radius: 22px; background: var(--sf-night-surface, #fff); box-shadow: 0 26px 70px var(--sf-night-shadow, rgba(10, 38, 55, .32)); animation: sfFaqAppear .22s ease both; }
        @keyframes sfFaqAppear { from { transform: translateY(12px) scale(.98); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .sf-faq-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 76px; padding: 13px 16px; border-bottom: 1px solid var(--sf-night-border, #d7eef1); background: linear-gradient(135deg, var(--sf-night-surface-alt, #e9fafb) 0%, var(--sf-night-brand-soft, #c8eff3) 100%); color: var(--sf-night-brand, #087f91); }
        .sf-faq-title-block { flex: 1; text-align: center; line-height: 1.1; }
        .sf-faq-title-block span { display: block; margin-bottom: 4px; color: var(--sf-night-brand, #4793a0); font-size: 10px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
        .sf-faq-title-block h2 { margin: 0; color: var(--sf-night-brand, #117e91); font-size: 20px; font-weight: 800; }
        .sf-faq-icon-button { display: inline-flex; width: 36px; height: 36px; align-items: center; justify-content: center; border: 0; border-radius: 11px; background: var(--sf-night-surface, rgba(255,255,255,.72)); color: var(--sf-night-brand, #168297); cursor: pointer; transition: transform .18s, background .18s; }
        .sf-faq-icon-button:hover { transform: translateY(-1px); background: var(--sf-night-surface, #fff); }
        .sf-faq-intro { display: flex; align-items: center; gap: 10px; padding: 15px 19px 11px; color: var(--sf-night-text-secondary, #5d7280); font-size: 13px; }
        .sf-faq-intro p { margin: 0; line-height: 1.4; }
        .sf-faq-intro-icon { display: grid; width: 31px; height: 31px; flex: 0 0 31px; place-items: center; border-radius: 10px; background: var(--sf-night-surface-alt, #e9f8fa); color: var(--sf-night-brand, #138ca1); }
        .sf-faq-search { display: flex; min-height: 45px; align-items: center; gap: 9px; margin: 0 18px 12px; padding: 0 12px; border: 1px solid var(--sf-night-border, #d7e8eb); border-radius: 14px; background: var(--sf-night-surface, #f9fcfc); color: var(--sf-night-text-secondary, #6d8790); transition: border-color .18s, box-shadow .18s; }
        .sf-faq-search:focus-within { border-color: var(--sf-night-brand, #26a6b7); background: var(--sf-night-surface, #fff); box-shadow: 0 0 0 3px var(--sf-night-focus-ring, rgba(38,166,183,.13)); }
        .sf-faq-search input { width: 100%; min-width: 0; border: 0; outline: 0; background: transparent; color: var(--sf-night-text, #263e4d); font-size: 13px; }
        .sf-faq-search input::placeholder { color: var(--sf-night-muted, #8ca0a7); }
        .sf-faq-search button { padding: 3px; border: 0; background: transparent; color: var(--sf-night-text-secondary, #718a91); cursor: pointer; }
        .sf-faq-scroll { overflow-y: auto; padding: 2px 18px 18px; scrollbar-color: #bfe5ea transparent; }
        .sf-faq-card { margin-bottom: 9px; overflow: hidden; border: 1px solid var(--sf-night-border, #e3edef); border-radius: 15px; background: var(--sf-night-surface, #fff); transition: border-color .18s, box-shadow .18s; }
        .sf-faq-card.is-open { border-color: var(--sf-night-border, #aee1e8); box-shadow: 0 8px 19px var(--sf-night-focus-ring, rgba(18,134,150,.10)); }
        .sf-faq-question { display: flex; width: 100%; align-items: center; gap: 10px; padding: 13px; border: 0; background: var(--sf-night-surface, #fff); color: var(--sf-night-text, #274254); text-align: left; cursor: pointer; }
        .sf-faq-question:hover { background: var(--sf-night-surface, #f7fcfc); }
        .sf-faq-question strong { flex: 1; font-size: 13px; font-weight: 700; line-height: 1.4; }
        .sf-faq-question > svg { flex: 0 0 auto; color: var(--sf-night-brand, #70a4ac); transition: transform .2s; }
        .sf-faq-question > svg.is-rotated { transform: rotate(180deg); color: var(--sf-night-brand, #168ca0); }
        .sf-faq-question-mark { display: grid; width: 29px; height: 29px; flex: 0 0 29px; place-items: center; border-radius: 9px; background: var(--sf-night-surface-alt, #e8f8fa); color: var(--sf-night-brand, #168ca0); }
        .sf-faq-answer { margin: 0; padding: 0 16px 15px 52px; color: var(--sf-night-text-secondary, #627783); font-size: 13px; line-height: 1.55; }
        .sf-faq-loading { padding: 32px 16px; color: var(--sf-night-text-secondary, #6a818b); text-align: center; font-size: 13px; }
        .sf-faq-empty { display: flex; min-height: 220px; flex-direction: column; align-items: center; justify-content: center; padding: 32px 20px; color: var(--sf-night-text-secondary, #687e88); text-align: center; }
        .sf-faq-empty > span { display: grid; width: 58px; height: 58px; margin-bottom: 13px; place-items: center; border-radius: 18px; background: var(--sf-night-surface-alt, #eaf9fb); color: var(--sf-night-brand, #1b9aac); }
        .sf-faq-empty h3 { margin: 0 0 7px; color: var(--sf-night-info, #294657); font-size: 15px; font-weight: 800; }
        .sf-faq-empty p { max-width: 270px; margin: 0; font-size: 13px; line-height: 1.5; }
        .sf-faq-empty button { margin-top: 15px; padding: 9px 13px; border: 0; border-radius: 10px; background: var(--sf-night-primary, #1c9dae); color: #fff; cursor: pointer; font-size: 12px; font-weight: 700; }
        @media (max-width: 640px) { .sf-faq-drawer { top: auto !important; right: 0 !important; bottom: 0 !important; left: 0 !important; width: 100%; max-height: min(760px, 92vh); border-radius: 22px 22px 0 0; } .sf-faq-scroll { padding-bottom: max(18px, env(safe-area-inset-bottom)); } }
      `}</style>
    </div>
  );
}
